import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../../api/client';
import { useCustomerList } from '../customers/hooks';
import { useVisitList } from '../visits/hooks';
import {
  usePackage,
  usePackageList,
  useResolveMergeValues,
  useSendAdHoc,
  useSendPackage,
  useTemplateList
} from './hooks';
import { usePageTitle } from '../../hooks/usePageTitle';
import type { PackageSend, TemplateDetail } from './types';

type SendType = 'package' | 'adhoc';
type Step = 'pick-type' | 'pick-source' | 'pick-recipient' | 'merge' | 'confirm' | 'done';

export function SendWizardPage() {
  usePageTitle('Send documents');
  const nav = useNavigate();
  const [step, setStep] = useState<Step>('pick-type');
  const [sendType, setSendType] = useState<SendType>('adhoc');
  const [packageId, setPackageId] = useState<string>('');
  const [adhocTemplateIds, setAdhocTemplateIds] = useState<string[]>([]);
  const [customerId, setCustomerId] = useState<string>('');
  const [visitId, setVisitId] = useState<string>('');
  const [mergeValues, setMergeValues] = useState<Record<string, Record<string, string>>>({});
  const [result, setResult] = useState<PackageSend | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolvedKeys, setResolvedKeys] = useState<Set<string>>(new Set());

  const { data: packages } = usePackageList();
  const { data: templates } = useTemplateList(false);
  const pkg = usePackage(sendType === 'package' ? packageId || undefined : undefined);
  const { data: customers } = useCustomerList({
    search: '',
    includeInactive: false,
    page: 1,
    pageSize: 100
  });
  const { data: visits } = useVisitList({});

  const sendAdhoc = useSendAdHoc();
  const sendPackage = useSendPackage();
  const resolveMerge = useResolveMergeValues();

  const selectedTemplateIds = useMemo(() => {
    if (sendType === 'package' && pkg.data) return pkg.data.items.map((i) => i.templateId);
    return adhocTemplateIds;
  }, [sendType, pkg.data, adhocTemplateIds]);

  // Load detail for each selected template so we can render the merge-field form.
  const templateDetails = useTemplateDetails(selectedTemplateIds);

  const anyEffectiveDateBound = templateDetails.some((t) => t?.isEffectiveDateBound === true);

  const allMergeFields = useMemo(
    () => Array.from(new Set(templateDetails.flatMap((t) => t?.mergeFields ?? []))),
    [templateDetails]
  );

  const TENANT_FIELDS = ['BusinessName', 'ContactName', 'ContactPhone', 'ContactEmail'];
  const missingTenantFields = allMergeFields.filter(
    (f) => TENANT_FIELDS.includes(f) && !resolvedKeys.has(f)
  );

  // Pre-fill the merge form from the selected customer/visit, then advance.
  const goToMerge = async () => {
    if (!customerId) return;
    try {
      const { resolved } = await resolveMerge.mutateAsync({
        customerId,
        visitId: visitId || null,
        fieldNames: allMergeFields
      });
      setResolvedKeys(new Set(Object.keys(resolved)));
      setMergeValues((prev) => {
        const next: Record<string, Record<string, string>> = { ...prev };
        for (const t of templateDetails) {
          if (!t) continue;
          const cur = { ...(next[t.id] ?? {}) };
          for (const f of t.mergeFields) {
            // never clobber a value the sitter already typed
            if ((cur[f] ?? '') === '' && resolved[f] !== undefined) cur[f] = resolved[f];
          }
          next[t.id] = cur;
        }
        return next;
      });
    } catch {
      // resolution is best-effort — fall through to manual entry
    }
    setStep('merge');
  };

  const onNextFromMerge = () => {
    if (anyEffectiveDateBound && !visitId) {
      setError('One of the selected templates is effective-date bound — pick a visit.');
      return;
    }
    setError(null);
    setStep('confirm');
  };

  const onSend = async () => {
    setError(null);
    try {
      if (sendType === 'package') {
        const out = await sendPackage.mutateAsync({
          packageId,
          customerId,
          visitId: visitId || null,
          mergeValues
        });
        setResult(out);
      } else {
        const out = await sendAdhoc.mutateAsync({
          customerId,
          visitId: visitId || null,
          items: adhocTemplateIds.map((tid) => ({
            templateId: tid,
            mergeValues: mergeValues[tid] ?? {}
          }))
        });
        setResult(out);
      }
      setStep('done');
    } catch (err) {
      setError(readMessage(err) ?? 'Send failed — try again.');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div>
        <Link to="/documents" className="text-sm text-slate-500 hover:text-sky-600">← Documents</Link>
        <h1 className="text-2xl font-semibold text-slate-800 mt-2">Send documents</h1>
      </div>

      <Stepper current={step} />

      {step === 'pick-type' && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-800">What are you sending?</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <TypeCard
              active={sendType === 'package'}
              title="Bundle"
              description="A pre-configured bundle of templates"
              onClick={() => { setSendType('package'); setStep('pick-source'); }}
            />
            <TypeCard
              active={sendType === 'adhoc'}
              title="Ad-hoc"
              description="Pick individual templates on the fly"
              onClick={() => { setSendType('adhoc'); setStep('pick-source'); }}
            />
          </div>
        </section>
      )}

      {step === 'pick-source' && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          {sendType === 'package' ? (
            <label className="block">
              <span className="block text-sm font-medium text-slate-700 mb-1">Bundle</span>
              <select
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
              >
                <option value="">Pick a bundle…</option>
                {packages?.filter((p) => p.isActive).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>
          ) : (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Templates</p>
              <div className="space-y-1">
                {templates?.filter((t) => t.isActive).map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={adhocTemplateIds.includes(t.id)}
                      onChange={(e) => {
                        setAdhocTemplateIds((s) =>
                          e.target.checked ? [...s, t.id] : s.filter((id) => id !== t.id)
                        );
                      }}
                    />
                    {t.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-between">
            <button type="button" onClick={() => setStep('pick-type')} className="text-slate-600 hover:text-sky-600 text-sm">← Back</button>
            <button
              type="button"
              onClick={() => setStep('pick-recipient')}
              disabled={sendType === 'package' ? !packageId : adhocTemplateIds.length === 0}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
            >
              Next →
            </button>
          </div>
        </section>
      )}

      {step === 'pick-recipient' && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Client</span>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
            >
              <option value="">Pick a customer…</option>
              {customers?.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}{c.email ? ` (${c.email})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">
              Visit {anyEffectiveDateBound && <span className="text-red-500">(required — selection includes a POA-class template)</span>}
            </span>
            <select
              value={visitId}
              onChange={(e) => setVisitId(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
            >
              <option value="">— none —</option>
              {visits
                ?.filter((v) => !customerId || v.customerId === customerId)
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    {new Date(v.startUtc).toLocaleString()} — {v.customerName}
                  </option>
                ))}
            </select>
          </label>
          <div className="flex justify-between">
            <button type="button" onClick={() => setStep('pick-source')} className="text-slate-600 hover:text-sky-600 text-sm">← Back</button>
            <button
              type="button"
              onClick={goToMerge}
              disabled={!customerId || resolveMerge.isPending}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
            >
              {resolveMerge.isPending ? 'Loading…' : 'Next →'}
            </button>
          </div>
        </section>
      )}

      {step === 'merge' && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-800">Fill merge fields</h2>
          <p className="text-sm text-slate-500">
            Fields are pre-filled from the selected client{visitId ? ' and visit' : ''}. Edit anything before sending.
          </p>
          {missingTenantFields.length > 0 && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              These business-profile fields aren&apos;t set yet: {missingTenantFields.join(', ')}.{' '}
              <Link to="/settings" className="underline font-medium">Complete your profile</Link>{' '}
              so they fill automatically. You can also type them below for this send.
            </p>
          )}
          {templateDetails.every((t) => !t || t.mergeFields.length === 0) && (
            <p className="text-sm text-slate-500 italic">None of the selected templates have merge fields.</p>
          )}
          {templateDetails.map((t) =>
            t && t.mergeFields.length > 0 ? (
              <div key={t.id} className="border border-slate-200 rounded p-4">
                <h3 className="font-medium text-slate-800 mb-2">{t.name}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {t.mergeFields.map((field) => (
                    <label key={field} className="block">
                      <span className="block text-sm font-medium text-slate-700 mb-1">{field}</span>
                      <input
                        type="text"
                        value={mergeValues[t.id]?.[field] ?? ''}
                        onChange={(e) =>
                          setMergeValues((s) => ({
                            ...s,
                            [t.id]: { ...(s[t.id] ?? {}), [field]: e.target.value }
                          }))
                        }
                        className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
                      />
                    </label>
                  ))}
                </div>
              </div>
            ) : null
          )}
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</p>}
          <div className="flex justify-between">
            <button type="button" onClick={() => setStep('pick-recipient')} className="text-slate-600 hover:text-sky-600 text-sm">← Back</button>
            <button
              type="button"
              onClick={onNextFromMerge}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 text-sm"
            >
              Next →
            </button>
          </div>
        </section>
      )}

      {step === 'confirm' && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-800">Confirm</h2>
          <ul className="text-sm text-slate-700 space-y-1">
            <li><strong>Type:</strong> {sendType === 'package' ? pkg.data?.name ?? 'bundle' : 'ad-hoc'}</li>
            <li><strong>Recipient:</strong> {customers?.items.find((c) => c.id === customerId)?.displayName}</li>
            <li><strong>Templates:</strong> {templateDetails.map((t) => t?.name).filter(Boolean).join(', ')}</li>
            {visitId && <li><strong>Visit:</strong> tied to the selected visit</li>}
          </ul>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</p>}
          <div className="flex justify-between">
            <button type="button" onClick={() => setStep('merge')} className="text-slate-600 hover:text-sky-600 text-sm">← Back</button>
            <button
              type="button"
              onClick={onSend}
              disabled={sendPackage.isPending || sendAdhoc.isPending}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
            >
              {(sendPackage.isPending || sendAdhoc.isPending) ? 'Sending…' : 'Send now'}
            </button>
          </div>
        </section>
      )}

      {step === 'done' && result && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
          <h2 className="text-lg font-semibold text-slate-800">Sent</h2>
          <p className="text-sm text-slate-700">
            Emailed {result.items.length} document{result.items.length === 1 ? '' : 's'} to{' '}
            <strong>{result.customerName}</strong>.
          </p>
          {result.magicLinkUrl ? (
            <div>
              <p className="text-xs uppercase text-slate-500 tracking-wide">Signing link (72h)</p>
              <code className="block bg-slate-50 border border-slate-200 rounded p-2 text-xs break-all">
                {result.magicLinkUrl}
              </code>
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              No signature required — sent as a confirmation copy with no signing link.
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => nav('/documents')}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 text-sm"
            >
              Done
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function useTemplateDetails(ids: string[]): Array<TemplateDetail | null> {
  // Fetch each template's detail once at step entry; keeps merge-field form
  // straightforward without a batch endpoint.
  const results: Array<TemplateDetail | null> = [];
  // We can't call hooks in a loop; instead fetch sequentially via useEffect.
  // For v1 limits (≤10 templates in a package), a simple join-on-list works:
  const [state, setState] = useState<Record<string, TemplateDetail | null>>({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next: Record<string, TemplateDetail | null> = {};
      for (const id of ids) {
        if (!id) continue;
        if (state[id]) {
          next[id] = state[id];
          continue;
        }
        try {
          const res = await apiClient.get<TemplateDetail>(`/api/v1/document-templates/${id}`);
          next[id] = res.data;
        } catch {
          next[id] = null;
        }
      }
      if (!cancelled) setState(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join('|')]);
  for (const id of ids) results.push(state[id] ?? null);
  return results;
}

function readMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data as { message?: string } | undefined;
  return data?.message ?? null;
}

function Stepper({ current }: { current: Step }) {
  const steps: Array<{ id: Step; label: string }> = [
    { id: 'pick-type', label: '1. Type' },
    { id: 'pick-source', label: '2. Pick' },
    { id: 'pick-recipient', label: '3. Recipient' },
    { id: 'merge', label: '4. Merge' },
    { id: 'confirm', label: '5. Confirm' }
  ];
  return (
    <ol className="flex flex-wrap gap-2 text-sm">
      {steps.map((s) => {
        const active = s.id === current;
        return (
          <li
            key={s.id}
            className={`px-3 py-1.5 rounded border ${
              active
                ? 'bg-sky-50 border-sky-300 text-sky-800 font-medium'
                : 'bg-white border-slate-200 text-slate-500'
            }`}
          >
            {s.label}
          </li>
        );
      })}
    </ol>
  );
}

function TypeCard({
  active,
  title,
  description,
  onClick
}: {
  active: boolean;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left border rounded-xl p-4 transition ${
        active ? 'border-sky-400 bg-sky-50' : 'border-slate-200 bg-white hover:border-sky-300'
      }`}
    >
      <div className="font-medium text-slate-800">{title}</div>
      <div className="text-sm text-slate-500">{description}</div>
    </button>
  );
}
